/**
 * @file The activity trail's vocabulary: the closed set of `audit_log.action`
 * values the API writes, the sentence each one reads as, and the endpoint's DTO.
 *
 * Shared so the API (which writes the constants) and the SPA (which renders them)
 * cannot disagree. A new action is a new entry in BOTH maps below; the unit test
 * fails when a constant has no sentence.
 *
 * No PII: a `summary` is built from ids and enums only (see apps/api/src/audit.ts).
 */
export const AUDIT_ACTIONS = {
  documentDownload: "document.download",
  paymentRecorded: "payment.recorded",
  expenseCreated: "expense.created",
  expenseDeleted: "expense.deleted",
  propertyDeleted: "property.deleted",
  unitDeleted: "unit.deleted",
  tenantUpdated: "tenant.updated",
  guarantorConsentChanged: "guarantor.consent_changed",
  memberRoleChanged: "member.role_changed",
  memberRemoved: "member.removed",
  workspaceRenamed: "workspace.renamed",
  ledgerExported: "ledger.exported",
  ledgerCorrected: "ledger.corrected",
  paymentsExported: "payments.exported",
  tenantsExported: "tenants.exported",
} as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[keyof typeof AUDIT_ACTIONS];

export const AUDIT_ACTION_VALUES: readonly AuditAction[] = Object.values(AUDIT_ACTIONS);

/** What each action reads as, in plain words. */
export const ACTIVITY_SENTENCES: Readonly<Record<AuditAction, string>> = {
  "document.download": "downloaded a document",
  "payment.recorded": "recorded a payment",
  "expense.created": "added an expense",
  "expense.deleted": "deleted an expense",
  "property.deleted": "deleted a property",
  "unit.deleted": "deleted a unit",
  "tenant.updated": "edited a tenant's details",
  "guarantor.consent_changed": "changed a guarantor's contact consent",
  "member.role_changed": "changed a team member's role",
  "member.removed": "removed a team member",
  "workspace.renamed": "renamed the workspace",
  "ledger.exported": "exported the ledger as CSV",
  "ledger.corrected": "corrected a ledger entry",
  "payments.exported": "exported payments as CSV",
  "tenants.exported": "exported tenants as CSV",
};

export function isAuditAction(value: string): value is AuditAction {
  return (AUDIT_ACTION_VALUES as readonly string[]).includes(value);
}

/** One row of GET /workspaces/:id/activity. */
export interface ActivityItemDto {
  readonly id: string;
  /** An `AuditAction` for rows this build wrote; any string for a row from a newer build. */
  readonly action: string;
  readonly entityType: string | null;
  readonly entityId: string | null;
  readonly summary: string | null;
  readonly actor: { readonly userId: string | null; readonly displayName: string | null };
  readonly createdAt: string;
}

export interface ActivityPageDto {
  readonly items: readonly ActivityItemDto[];
  readonly nextCursor: string | null;
}
